/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useRef, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  FlatList,
  TextInput,
  Dimensions,
  Platform,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../theme";
import { useDarkStyles } from '../../theme/useDarkStyles';

// Define item interface for type safety
interface DropdownItem {
  id: string | number;
  displayText: string;
  [key: string]: any;
}

// Define props interface
interface PTDropDownProps {
  childList: DropdownItem[];
  dropdownHeader?: string;
  feedbackType?: "info" | "error" | "success" | "warning";
  helperText?: string;
  isClearable?: boolean;
  isRequired?: boolean;
  label?: string;
  menuPosition?: string;
  placeholder?: string;
  selection?: "single" | "multi";
  setSelectedValue: (value: DropdownItem | DropdownItem[] | null) => void;
  selectedValue: DropdownItem | DropdownItem[] | null;
  size?: "small" | "medium" | "large";
  style?: any;
  className?: string;
  validate?: boolean;
  testID?: string;
  readOnly?: boolean;
}

const PTDropDown: React.FC<PTDropDownProps> = ({
  childList = [],
  dropdownHeader = "",
  feedbackType = "info",
  helperText = "",
  isClearable = false,
  isRequired = false,
  label = "",
  menuPosition = "bottom",
  placeholder = "Select...",
  selection = "single",
  setSelectedValue = () => {},
  selectedValue = null,
  size = "medium",
  style = {},
  validate = false,
  readOnly = false,
  testID,
}) => {
  const styles = useDarkStyles(baseStyles);
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  // Dark-mode color tokens (mobile only)
  const triggerBg = isDark ? '#141414' : 'white';
  const triggerBorder = isDark ? '#3A3A3A' : '#DEE2E6';
  const selectedTextColor = isDark ? colors.text.primary : '#343A40';
  const placeholderTextColor = isDark ? colors.text.secondary : '#6C757D';
  const arrowIconColor = isDark ? colors.text.primary : '#495057';
  const menuBg = isDark ? '#141414' : 'white';
  const menuBorder = isDark ? '#3A3A3A' : '#E9ECEF';
  const searchIconColor = isDark ? colors.text.secondary : '#495057';
  const searchInputColor = isDark ? colors.text.primary : '#343A40';
  const searchPlaceholderColor = isDark ? colors.text.secondary : '#6C757D';
  const itemBorderColor = isDark ? '#2A2A2A' : '#F1F3F5';
  const itemSelectedBg = isDark ? '#1A3A5C' : '#F8F9FA';
  const itemHoverBg = isDark ? '#1C1C1C' : '#E9ECEF';
  const itemTextColor = isDark ? colors.text.primary : '#343A40';
  const checkColor = isDark ? '#60A5FA' : '#0066CC';
  const emptyTextColor = isDark ? colors.text.secondary : '#6C757D';
  const headerTextColor = isDark ? colors.text.primary : '#343A40';
  const headerBorderColor = isDark ? '#3A3A3A' : '#E9ECEF';
  const modalOverlayBg = isDark ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.2)';

  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<DropdownItem[]>(
    Array.isArray(selectedValue) ? (selectedValue as DropdownItem[]) : selectedValue ? [selectedValue as DropdownItem] : []
  );
  const [searchText, setSearchText] = useState("");
  const dropdownRef = useRef<any>(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    flipped: false,
    triggerTop: 0,
  });
  const [hoveredItemId, setHoveredItemId] = useState<string | number | null>(
    null
  );
  const [isValidated, setIsValidated] = useState(false);

  useEffect(() => {
    if (selectedValue) {
      setSelectedItems(Array.isArray(selectedValue) ? (selectedValue as DropdownItem[]) : [selectedValue as DropdownItem]);
    }
  }, [selectedValue, isOpen]);

  useEffect(() => {
    if (validate) {
      setIsValidated(true);
    }
  }, [validate]);

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchText('');
  };

  const toggleDropdown = () => {
    if (readOnly) {
      return;
    }
    if (!isOpen) {
      const APPROX_MENU_HEIGHT = 220;
      const screenH = Dimensions.get("window").height;

      if (Platform.OS !== "web") {
        if (
          dropdownRef.current &&
          typeof dropdownRef.current.measureInWindow === "function"
        ) {
          dropdownRef.current.measureInWindow(
            (x: number, y: number, width: number, height: number) => {
              const spaceBelow = screenH - (y + height);
              const flipped = spaceBelow < APPROX_MENU_HEIGHT + 8;
              setDropdownPosition({
                top: flipped
                  ? y - APPROX_MENU_HEIGHT
                  : y + height + (Platform.OS === "ios" ? 8 : 0),
                left: x,
                width: width,
                flipped,
                triggerTop: y,
              });
              setIsOpen(true);
            }
          );
        } else {
          setIsOpen(true);
        }
      } else {
        try {
          if (
            dropdownRef.current &&
            typeof dropdownRef.current.getBoundingClientRect === "function"
          ) {
            const rect = dropdownRef.current.getBoundingClientRect();
            const scrollY =
              typeof window !== "undefined" ? window.scrollY || 0 : 0;

            const spaceBelow = screenH - rect.bottom;
            const flipped = spaceBelow < APPROX_MENU_HEIGHT + 8;
            setDropdownPosition({
              top: flipped
                ? rect.top + scrollY - APPROX_MENU_HEIGHT
                : rect.bottom + scrollY,
              left: rect.left,
              width: rect.width,
              flipped,
              triggerTop: rect.top + scrollY,
            });
          }
          setIsOpen(true);
        } catch (error) {
          console.warn("Error positioning dropdown:", error);
          setIsOpen(true);
        }
      }
    } else {
      closeDropdown();
    }
  };

  const handleSelect = (item: DropdownItem) => {
    if (selection === "single") {
      setSelectedItems([item]);
      setSelectedValue(item);
      setIsOpen(false);
      setSearchText(""); // Clear search on selection
    } else {
      const isSelected = selectedItems.some(
        (selected) => selected.id === item.id
      );
      if (isSelected) {
        const newSelection = selectedItems.filter(
          (selected) => selected.id !== item.id
        );
        setSelectedItems(newSelection);
        setSelectedValue(newSelection);
      } else {
        const newSelection = [...selectedItems, item];
        setSelectedItems(newSelection);
        setSelectedValue(newSelection);
      }
    }
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setSelectedValue(null);
  };

  const filteredItems = childList.filter((item) =>
    item.displayText.toLowerCase().includes(searchText.toLowerCase())
  );

  const renderItem = ({ item }: { item: DropdownItem }) => {
    const isSelected = selectedItems.some((sel) => sel.id === item.id);
    const isHovered = Platform.OS === "web" && hoveredItemId === item.id;
    return (
      <TouchableOpacity
        style={[
          styles.item,
          { borderBottomColor: itemBorderColor },
          isSelected && { backgroundColor: itemSelectedBg },
          isHovered && !isSelected && { backgroundColor: itemHoverBg },
        ]}
        onPress={() => handleSelect(item)}
        {...(Platform.OS === "web"
          ? {
              onMouseEnter: () => setHoveredItemId(item.id),
              onMouseLeave: () => setHoveredItemId(null),
            }
          : {})}
      >
        <Text style={[styles.itemText, { color: itemTextColor }]}>{item.displayText}</Text>
        {isSelected && (
          <MaterialIcons name="check" size={20} color={checkColor} />
        )}
      </TouchableOpacity>
    );
  };

  const getDisplayText = () => {
    if (selectedItems.length === 0) {
      return placeholder;
    }
    if (selection === "single") {
      return selectedItems[0].displayText;
    }
    return selectedItems.map((item) => item.displayText).join(", ");
  };

  return (
    <View style={[styles.container, style]}>
      {label && (
        <Text style={styles.label}>
          {label}
          {isRequired && <Text style={styles.required}> *</Text>}
        </Text>
      )}

      <TouchableOpacity
        ref={dropdownRef}
        style={[
          styles.dropdownTrigger,
          { backgroundColor: triggerBg, borderColor: triggerBorder },
          size === "small" && styles.smallTrigger,
          size === "large" && styles.largeTrigger,
          readOnly && { opacity: 0.5 },
        ]}
        onPress={toggleDropdown}
        disabled={readOnly}
        testID={testID}
      >
        <Text
          style={[
            styles.selectedText,
            { color: selectedItems.length === 0 ? placeholderTextColor : selectedTextColor },
          ]}
          numberOfLines={1}
        >
          {getDisplayText()}
        </Text>
        <MaterialIcons
          name={isOpen ? "arrow-drop-up" : "arrow-drop-down"}
          size={20}
          color={arrowIconColor}
        />
        {isClearable && selectedItems.length > 0 && (
          <TouchableOpacity
            style={styles.clearButton}
            onPress={(e: any) => {
              e.stopPropagation();
              clearSelection();
            }}
          >
            <MaterialIcons name="close" size={18} color="#495057" />
          </TouchableOpacity>
        )}
      </TouchableOpacity>

      {helperText && (
        <Text
          style={[
            styles.helperText,
            feedbackType === "error" && styles.helperTextError,
            feedbackType === "success" && styles.helperTextSuccess,
            feedbackType === "warning" && styles.helperTextWarning,
          ]}
        >
          {helperText}
        </Text>
      )}

      <Modal
        visible={isOpen}
        transparent
        animationType="fade"
        supportedOrientations={["portrait", "landscape"]}
        onRequestClose={closeDropdown}
      >
        <TouchableOpacity
          style={[styles.modalOverlay, { backgroundColor: modalOverlayBg }]}
          activeOpacity={1}
          onPress={closeDropdown}
        >
          <TouchableOpacity
            activeOpacity={1}
            onPress={(e: any) => e.stopPropagation()}
          >
            <View
              style={[
                styles.dropdownMenu,
                { backgroundColor: menuBg, borderColor: menuBorder, borderWidth: 1 },
                {
                  top: dropdownPosition.top,
                  left: menuPosition.includes("right")
                    ? dropdownPosition.left + dropdownPosition.width - dropdownPosition.width
                    : dropdownPosition.left,
                  width: dropdownPosition.width || 200,
                },
              ]}
            >
              {dropdownHeader && (
                <Text style={[styles.dropdownHeader, { color: headerTextColor, borderBottomColor: headerBorderColor }]}>{dropdownHeader}</Text>
              )}

              <View style={[styles.searchContainer, { backgroundColor: menuBg, borderBottomColor: menuBorder }]}>
                <MaterialIcons
                  name="search"
                  size={20}
                  color={searchIconColor}
                  style={styles.searchIcon}
                />
                <TextInput
                  style={[styles.searchInput, { color: searchInputColor, backgroundColor: isDark ? '#141414' : undefined }]}
                  placeholder="Search..."
                  value={searchText}
                  onChangeText={setSearchText}
                  placeholderTextColor={searchPlaceholderColor}
                />
              </View>

              <FlatList
                data={filteredItems}
                renderItem={renderItem}
                keyExtractor={(item) => item.id.toString()}
                style={[styles.list, { backgroundColor: menuBg }]}
                keyboardShouldPersistTaps="handled"
                ListEmptyComponent={
                  <Text style={[styles.emptyText, { color: emptyTextColor }]}>No items found</Text>
                }
              />
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const baseStyles = StyleSheet.create({
  container: {
    marginBottom: 16,
    width: "100%",
  },
  label: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#343A40",
    marginBottom: 6,
  },
  required: {
    color: "#DC3545",
  },
  dropdownTrigger: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: "#DEE2E6",
    borderRadius: 4,
    backgroundColor: "white",
    minHeight: 40,
  },
  smallTrigger: {
    minHeight: 32,
    paddingVertical: 6,
  },
  largeTrigger: {
    minHeight: 48,
    paddingVertical: 12,
  },
  selectedText: {
    flex: 1,
    fontSize: 16,
    color: "#343A40",
    marginRight: 8,
  },
  placeholderText: {
    color: "#6C757D",
  },
  clearButton: {
    marginRight: 8,
  },
  helperText: {
    fontSize: 12,
    marginTop: 4,
    color: "#0066CC",
  },
  helperTextError: {
    color: "#DC3545",
  },
  helperTextSuccess: {
    color: "#28A745",
  },
  helperTextWarning: {
    color: "#FFC107",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.2)",
  },
  dropdownMenu: {
    position: "absolute",
    backgroundColor: "white",
    borderRadius: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 5,
    maxHeight: Dimensions.get("window").height * 0.6,
    overflow: "hidden",
  },
  dropdownHeader: {
    padding: 12,
    fontSize: 16,
    fontWeight: "600",
    color: "#343A40",
    borderBottomWidth: 1,
    borderBottomColor: "#E9ECEF",
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#E9ECEF",
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: "#343A40",
    paddingVertical: 4,
  },
  list: {
    paddingHorizontal: 4,
    maxHeight: 144,
  },
  item: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#F1F3F5",
  },
  selectedItem: {
    backgroundColor: "#F8F9FA",
  },
  hoverItem: {
    backgroundColor: "#E9ECEF",
  },
  itemIcon: {
    marginRight: 12,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 12,
  },
  itemText: {
    flex: 1,
    fontSize: 16,
    color: "#343A40",
    marginRight: 8,
  },
  emptyText: {
    padding: 16,
    textAlign: "center",
    color: "#6C757D",
  },
});

export default PTDropDown;
